import { useState, useMemo, Dispatch, SetStateAction } from "react";
import { IGeneratedImage } from "pages/imageCreator/components/ImagePreview";
import { type StoredFile } from "hooks/useProcessFiles";
import { convertImageToStoredFile } from "utils";
import { toPascalCase, parseAndAppendSupportCode } from "utils/function";
import type {
  IAspectRatioe,
  ISampleCount,
} from "pages/imageCreator/components/ExtraAction";
// import useGoogleAnalytics from "./useGoogleAnalytics";
import { IImageStyle } from "data/attach-styles";
import { imageGenerationCategories } from "data/image-categories";
import { MixpanelAnalytics } from "utils/mixpanel";
import ReactGA from "react-ga4";

import {
  getUserCredit,
  UserCredit,
  CREDITS_PER_IMAGE,
} from "utils/creditUtils";
import { errors, type ErrorDetails, errorCodes } from "data/request-error-code";

// InlineDataItem 类型
// type InlineDataItem = {
//   mimeType: string;
//   data: string;
// };

// interface ContentPart {
//   inlineData?: InlineDataItem;
//   text?: string;
// }

// interface JsonData {
//   content: {
//     parts: ContentPart[];
//     role: string;
//   };
// }

// Interface for responseData (for type safety)
interface ResponseData {
  predictions: {
    mimeType: string;
    bytesBase64Encoded: string;
  }[];
}

export interface IImageGen3Instances {
  prompt: string;
  referenceImages?: IImageGen3ReferenceImage[];
}

export interface IImageGen3Parameters {
  sampleCount: number;
  safetySetting?:
    | "block_none"
    | "block_only_high"
    | "block_medium_and_above"
    | "block_low_and_above";
  aspectRatio?: string;
  editMode?:
    | "EDIT_MODE_INPAINT_REMOVAL"
    | "EDIT_MODE_INPAINT_INSERTION"
    | "EDIT_MODE_OUTPAINT"
    | "EDIT_MODE_BGSWAP";
}

// 定义 ReferenceType 作为类型别名
type ReferenceType =
  | "REFERENCE_TYPE_RAW"
  | "REFERENCE_TYPE_MASK"
  | "REFERENCE_TYPE_SUBJECT"
  | "REFERENCE_TYPE_STYLE"
  | "REFERENCE_TYPE_CONTROL";

export interface IImageGen3ReferenceImage {
  referenceType: ReferenceType;
  referenceId: number;
  referenceImage: {
    bytesBase64Encoded: string;
  };
  maskImageConfig?: {
    maskMode:
      | "MASK_MODE_USER_PROVIDED"
      | "MASK_MODE_BACKGROUND"
      | "MASK_MODE_SEMANTIC";
  };
}

export interface IImageGen3Payload {
  user_id: number;
  email: string;
  model: string;
  credits: number;
  instances: IImageGen3Instances[];
  parameters: IImageGen3Parameters;
}

// Payload 类型
// type ImageFlashPayload = {
//   user_id: number;
//   email: string;
//   contents: {
//     role: "user";
//     parts: ({ text: string } | { inlineData: InlineDataItem })[];
//   }[];
// };

export type IFetchErrorMessage = ErrorDetails | undefined;

interface CustomEventParams {
  category?: string;
  action: string;
  label?: string;
  value?: number;
  [key: string]: any; // 允许任意自定义参数
}

type IGemerateImageProp = {
  generationImages?: IGeneratedImage;
  isEditingImage?: boolean;
  isLoading?: boolean;
  disabled: boolean;
  fetchErrorMessage?: IFetchErrorMessage;
  setGenerationImages: Dispatch<SetStateAction<IGeneratedImage>>;
  onGenerateImage: (retry?: boolean) => void;
  onTestGenerateImageFetch?: () => void;
  resetErrorMessage?: () => void;
};

export interface FormDraft {
  message: string;
  selectedFiles?: Record<string, StoredFile>;
  selectedItem?: IImageStyle[];
  sampleCount?: ISampleCount;
  aspectRatio?: IAspectRatioe;
  isStorageCompleted: boolean;
  selectedImageStyleIndex?: number;
}

export const models = {
  image_generation: "imagen-3.0-generate-002",
  image_editing: "imagen-3.0-capability-001",
} as const;

// 提取事件名类型
export type Models = (typeof models)[keyof typeof models];

// export const modelId = "image"; //"imagen-3.0-capability-001";

export const useGenerateImage = ({
  userCredits,
  selectedFiles,
  creditInfo,
  useMessage,
  sampleCount,
  message,
  isOverMaxCharLength,
  selectedItem,
  selectedImageStyleIndex,
  aspectRatio,
  model,
  setModel,
  onGetUserCredits,
  handleScroll,
  setShowLoginModal,
  setShowUpgradeModal,
}: {
  userCredits: number;
  selectedFiles: Record<string, StoredFile>;
  creditInfo: UserCredit;
  useMessage: string;
  sampleCount: ISampleCount;
  message: string;
  isOverMaxCharLength: boolean;
  selectedItem: IImageStyle[];
  selectedImageStyleIndex: number;
  aspectRatio: IAspectRatioe;
  model: Models;
  setModel: Dispatch<SetStateAction<Models>>;
  onGetUserCredits: () => void;
  handleScroll: () => void;
  setShowLoginModal: Dispatch<SetStateAction<boolean>>;
  setShowUpgradeModal: Dispatch<SetStateAction<boolean>>;
}): IGemerateImageProp => {
  // const { modelId } = useGoogleAnalytics(); //trackEvent

  const [fetchErrorMessage, setFetchErrorMessage] =
    useState<IFetchErrorMessage>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [generationImages, setGenerationImages] =
    useState<IGeneratedImage>(undefined);

  const isEditingImage = useMemo(
    () => !!generationImages?.editFileId,
    [generationImages]
  );

  // 检查是否存在验证不通过的文件
  const hasInvalidFiles = (files: Record<string, StoredFile>): boolean => {
    return Object.values(files).some((file) => Boolean(file.error));
  };

  // 优化后的按钮状态
  const isGenerateButtonDisabled = useMemo(() => {
    // 没有消息或消息为空时禁用
    if (!message?.trim()) {
      return true;
    }

    // 加载中时禁用
    if (isLoading) {
      return true;
    }

    // 存在prompt字符串超长问题
    if (isOverMaxCharLength) {
      return true;
    }

    // 存在验证不通过的文件时禁用
    return hasInvalidFiles(selectedFiles);
  }, [isLoading, message, selectedFiles, isOverMaxCharLength]);

  // // 将 selectedFiles 转换为数组格式，每个元素包含 inlineData 对象
  // const convertSelectedFilesToInlineData = (
  //   selectedFiles: Record<string, StoredFile>
  // ): Array<{ inlineData: { mimeType: string; data: string } }> => {
  //   const imageData = Object.values(selectedFiles)
  //     .filter((file) => file.preview && !file.error) // 只处理有预览且无错误的文件
  //     .map((file) => {
  //       const base64Data = file.preview.split(",")[1] || ""; // 提取纯 base64 数据
  //       const mimeType = `image/${file.extension?.toLowerCase()}`; // 设置 mimeType

  //       return {
  //         inlineData: {
  //           mimeType,
  //           data: base64Data,
  //         },
  //       };
  //     });

  //   // 无论是否有有效文件，都返回数组，如果没有则为空数组
  //   return imageData.length > 0 ? imageData : [];
  // };

  // const validateBase64 = (base64String:string) =>{
  //   try {
  //     // Base64 格式正则表达式
  //     const base64Regex = /^[A-Za-z0-9+/=]+$/;
  //     if (!base64Regex.test(base64String)) {
  //       throw new Error('Invalid Base64 format: contains invalid characters.');
  //     }

  //     // 解码测试
  //     const binary = atob(base64String);
  //     if (!binary) {
  //       throw new Error('Base64 decoding failed.');
  //     }

  //     // 可选：尝试加载为图像以验证完整性
  //     return new Promise((resolve, reject) => {
  //       const img = new Image();
  //       img.onload = () => {
  //         console.log('Base64 data is a valid image.');
  //         resolve(true);
  //       };
  //       img.onerror = () => reject(new Error('Base64 data is not a valid image.'));
  //       img.src = `data:image/jpeg;base64,${base64String}`; // 根据实际格式调整
  //     });
  //   } catch (error:any) {
  //     console.error('Base64 validation error:', error?.message);
  //     throw error;
  //   }
  // }

  const convertSelectedFilesToReferenceData = (
    selectedFiles: Record<string, StoredFile>
  ): Array<{
    referenceType: ReferenceType;
    referenceId: number;
    referenceImage: { bytesBase64Encoded: string };
  }> => {
    const referenceData = Object.values(selectedFiles)
      .filter((file) => file.preview && !file.error) // 只处理有预览且无错误的文件
      .map((file, index) => {
        const base64Data = file.preview.split(",")[1] || ""; // 提取纯 base64 数据
        const referenceId = index + 1; // 使用从 1 开始的下标值

        // validateBase64(base64Data)

        return {
          referenceType: "REFERENCE_TYPE_RAW" as ReferenceType, // 固定值
          referenceId, // 从 1 开始的索引
          referenceImage: {
            bytesBase64Encoded: base64Data, // base64 编码的图像数据
          },
        };
      });

    // 无论是否有有效文件，都返回数组，如果没有则为空数组
    return referenceData.length > 0 ? referenceData : [];
  };

  // 提取所有图片数据
  // const extractAllImages = (candidates: JsonData[]): StoredFile[] => {
  //   return candidates.flatMap((candidate) =>
  //     candidate.content.parts
  //       .filter(
  //         (part): part is ContentPart & { inlineData: InlineDataItem } =>
  //           !!part.inlineData &&
  //           !!part.inlineData.mimeType &&
  //           !!part.inlineData.data
  //       )
  //       .map((part) => {
  //         const { mimeType, data } = part.inlineData;
  //         const base64Data = `data:${mimeType};base64,${data}`;
  //         const storeFile = convertImageToStoredFile(base64Data);
  //         return storeFile;
  //       })
  //   );
  // };

  // 提取所有文本数据
  // const extractAllText = (candidates: JsonData[]): string[] => {
  //   return candidates.flatMap((candidate) =>
  //     candidate.content.parts
  //       .filter(
  //         (part): part is ContentPart & { text: string } =>
  //           typeof part.text === "string" && part.text.trim().length > 0
  //       )
  //       .map((part) => part.text.trim())
  //   );
  // };

  // Extracts all images from responseData and converts to StoredFile[]
  const extractAllImagesFromPredictions = (
    responseData: ResponseData
  ): StoredFile[] => {
    return responseData.predictions
      .filter(
        (prediction) => !!prediction.mimeType && !!prediction.bytesBase64Encoded
      )
      .map((prediction) => {
        const { mimeType, bytesBase64Encoded } = prediction;
        const base64Data = `data:${mimeType};base64,${bytesBase64Encoded}`;
        const storeFile = convertImageToStoredFile(base64Data);
        return storeFile;
      });
  };

  // API测试接口
  const onTestGenerateImageFetch = () => {
    setIsLoading(true);
    // setFetchErrorMessage(undefined);

    handleScroll();

    const params: CustomEventParams = {
      category: toPascalCase(
        imageGenerationCategories[selectedImageStyleIndex].name
      ),
      action: "image_generation_started",
      prompt_length: message.length,
      style_tags_count: selectedItem.length,
      attached_image_count: Object.keys(selectedFiles).length,
    };

    ReactGA.event(params as any);

    MixpanelAnalytics.trackEvent("image_generation_started", {
      prompt_length: message.length,
      style_tags_count: selectedItem.length,
      attached_image_count: Object.keys(selectedFiles).length,
      category: imageGenerationCategories[selectedImageStyleIndex].name,
    });

    const imageGenerateStartTime = new Date().getTime();

    fetch(process.env.PUBLIC_URL + "/api-response-image3-error.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setIsLoading(false);

        // flash image generation
        // const candidates = data.candidates;
        // const images = extractAllImagesFromPredictions(candidates);
        // if (images.length === 0) {
        //   const texts = extractAllText(candidates);
        //   setFetchErrorMessage(texts.join("\n"));
        // }

        // image3 image generation
        const predictions = data;
        const imageGenerateEndTime = new Date().getTime();

        if (Object.keys(predictions).length === 0) {
          setFetchErrorMessage({
            message: "No image generated",
            prompt: "This is the user prompt for testing the API",
          });
          return;
        }

        if (data.error) {
          if (data.error?.message) {
            const errorCode = parseAndAppendSupportCode(data.error.message);

            const errorMessage =
              errorCode && errorCodes[errorCode]
                ? `${data.error.message} - ${errorCodes[errorCode].description}`
                : data.error.message;

            setFetchErrorMessage({
              message: errorMessage,
              prompt: "This is the user prompt for testing the API",
            });

            return;
          }
        }

        const images = extractAllImagesFromPredictions(predictions);
        if (images.length === 0) {
          setFetchErrorMessage({
            message: "No image generated",
            prompt: "This is the user prompt for testing the API",
          });
          // const texts = extractAllText(predictions);
          // setFetchErrorMessage(texts.join("\n"));
        }

        const params: any = {
          action: "image_generation_completed",
          generation_time: imageGenerateEndTime - imageGenerateStartTime,
          model_id: model,
          success: true,
          aspect_ratio: aspectRatio.value,
          sample_count: sampleCount.value,
          category: toPascalCase(
            imageGenerationCategories[selectedImageStyleIndex].name
          ),
        };

        ReactGA.event(params);
        MixpanelAnalytics.trackEvent("image_generation_completed", {
          generation_time: imageGenerateEndTime - imageGenerateStartTime,
          model_id: model,
          success: true,
          aspect_ratio: aspectRatio.value,
          sample_count: sampleCount.value,
        });

        setGenerationImages({
          prompt: "This is the user prompt for testing the API",
          images,
        });
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          console.error("Error loading animation:", error);
        }
      });
  };

  // 保存表单数据到 localStorage
  const saveFormData = (data: FormDraft) => {
    try {
      const newData: FormDraft = {
        ...data,
        isStorageCompleted: true,
      };

      localStorage.setItem("formDraft", JSON.stringify(newData));
    } catch (error) {
      localStorage.setItem(
        "formDraft",
        JSON.stringify({
          ...data,
          selectedFiles: {},
          isStorageCompleted: false,
        })
      );
    }
  };

  // 图像生成请求
  const onGenerateImage = (retry?: boolean) => {
    setFetchErrorMessage(undefined);
    setGenerationImages(undefined);

    if (isGenerateButtonDisabled) return;

    // Credit
    const currentCredit = getUserCredit();

    const imageGenerateStartTime = new Date().getTime();

    // 将附件转换为目标格式
    const inlineDataObject = convertSelectedFilesToReferenceData(selectedFiles);

    const baseURL =
      process.env.REACT_APP_API_URL ||
      "https://gemini-flash.nekteck.workers.dev";

    handleScroll();

    // const body: ImageFlashPayload = {
    //   user_id: creditInfo.user_id || 0,
    //   email: creditInfo.email || "",
    //   contents: [
    //     {
    //       role: "user",
    //       parts: [{ text: useMessage }, ...inlineDataObject],
    //     },
    //   ],
    // };

    const generateModel =
      inlineDataObject.length > 0
        ? models.image_editing
        : models.image_generation;

    
    setModel(generateModel);

    // image3
    const body: IImageGen3Payload = {
      user_id: creditInfo.user_id || 0,
      email: creditInfo.email || "",
      model: generateModel,
      credits: Math.max(sampleCount.value, 0) * CREDITS_PER_IMAGE,
      parameters: {
        sampleCount: sampleCount.value,
        aspectRatio: aspectRatio.value,
        safetySetting: "block_low_and_above"
      },
      instances: [
        {
          prompt: useMessage,
        },
      ],
    };

    if (inlineDataObject.length > 0) {
      body.instances[0].referenceImages = inlineDataObject;
      body.parameters.safetySetting = "block_only_high";
    }

    // 未登录用户（token 为空）积分不足
    if (currentCredit.token === "") {
      // 保存
      const replayload: FormDraft = {
        selectedFiles,
        message,
        isStorageCompleted: true,
        selectedItem,
        sampleCount,
        aspectRatio,
        selectedImageStyleIndex,
      };

      if (!retry) saveFormData(replayload);

      setShowLoginModal(true);
      return;
    }

    // 已登录用户积分不足
    if (
      currentCredit.token !== "" &&
      userCredits < Math.max(sampleCount.value, 0) * CREDITS_PER_IMAGE &&
      !retry
    ) {
      // 保存
      const replayload: FormDraft = {
        selectedFiles,
        message,
        isStorageCompleted: true,
        selectedItem,
        sampleCount,
        aspectRatio,
        selectedImageStyleIndex,
      };

      if (!retry) saveFormData(replayload);

      setShowUpgradeModal(true);
      return;
    }

    setIsLoading(true);

    const params: CustomEventParams = {
      category: toPascalCase(
        imageGenerationCategories[selectedImageStyleIndex].name
      ),
      action: "image_generation_started",
      prompt_length: message.length,
      style_tags_count: selectedItem.length,
      attached_image_count: Object.keys(selectedFiles).length,
    };

    ReactGA.event(params as any);

    MixpanelAnalytics.trackEvent("image_generation_started", {
      prompt_length: message.length,
      style_tags_count: selectedItem.length,
      attached_image_count: Object.keys(selectedFiles).length,
      category: imageGenerationCategories[selectedImageStyleIndex].name,
    });

    let requestStatus: number | undefined = undefined;

    fetch(baseURL, {
      method: "POST",
      headers: {
        token: creditInfo.token,
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        // 检查响应状态
        if (!response.ok) {
          // 非 200-299 的状态码

          const status = response.status;

          requestStatus = status;

          if (status === 401) {
            setShowLoginModal(true);
          }

          const errorText = response.json(); // 获取错误信息（文本格式）
          return errorText;
        }

        return response.json();
      })
      .then((data) => {
        setIsLoading(false);
        const imageGenerateEndTime = new Date().getTime();
        // setMessage("");
        // setSelectedFiles({});

        // image3
        if (Object.keys(data).length === 0) {
          const errorObject = errors["INVALID_ARGUMENT"];
          setFetchErrorMessage({
            prompt: message,
            title: errorObject.title,
            message: errorObject.message,
          });
          const params: CustomEventParams = {
            action: "image_generation_completed",
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: false,
            aspect_ratio: aspectRatio.value,
            sample_count: sampleCount.value,
          };

          ReactGA.event(params as any);
          MixpanelAnalytics.trackEvent("image_generation_completed", {
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: false,
            aspect_ratio: aspectRatio.value,
            sample_count: sampleCount.value,
            category: "ImageGenerationFailed",
          });
          return;
        }

        if (data.predictions) {
          const images = extractAllImagesFromPredictions(data);
          if (images.length === 0) {
            const errorObject = errors["INVALID_ARGUMENT"];
            setFetchErrorMessage({
              prompt: message,
              title: errorObject.title,
              message: errorObject.message,
            });
          } else {
            setGenerationImages({ prompt: message, images });
          }

          const params: CustomEventParams = {
            action: "image_generation_completed",
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: true,
            aspect_ratio: aspectRatio.value,
            sample_count: sampleCount.value,
            category: "ImageGenerationSuccess",
          };

          ReactGA.event(params as any);

          MixpanelAnalytics.trackEvent("image_generation_completed", {
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: true,
            aspect_ratio: aspectRatio.value,
            sample_count: sampleCount.value,
          });
        } else if (data.error) {
          let errorMessage = data?.error?.message;

          if (data.error?.message) {
            const errorCode = parseAndAppendSupportCode(data.error.message);

            if (errorCode && errorCodes[errorCode]) {
              errorMessage = `${data.error.message} - ${errorCodes[errorCode].description}`;
            }

            const errorObject =
              data?.error?.code && errors[data?.error?.status]
                ? errors[data?.error?.status]
                : errors["INVALID_ARGUMENT"];

            setFetchErrorMessage({
              message: errorMessage,
              title: errorObject.title,
              prompt: message,
            });
          }

          const params: CustomEventParams = {
            action: "image_generation_failed",
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: false,
            error_http_code: requestStatus,
            error_code: data?.error?.status,
            error_message: errorMessage,
            category: "ImageGenerationFailed",
          };

          ReactGA.event(params as any);

          MixpanelAnalytics.trackEvent("image_generation_failed", {
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: false,
            error_http_code: requestStatus,
            error_code: data?.error?.status,
            error_message: errorMessage,
          });
        } else if (data?.message) {
            setFetchErrorMessage({
              message: data?.message,
              prompt: message,
            });

          const params: CustomEventParams = {
            action: "image_generation_failed",
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: false,
            error_http_code: requestStatus,
            error_message: data?.message,
            category: "ImageGenerationFailed",
          };

          ReactGA.event(params as any);

          MixpanelAnalytics.trackEvent("image_generation_failed", {
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: false,
            error_http_code: requestStatus,
            error_message: data?.message,
          });
        }
        onGetUserCredits();

        // flash image generateion
        // const candidates = data.candidates;
        // if (candidates) {
        //   const images = extractAllImages(candidates);

        //   if (images.length > 0) {
        //     setGenerationImages({ prompt: message, images });
        //   } else {
        //     const texts = extractAllText(candidates);
        //     setFetchErrorMessage(texts.join("\n"));
        //   }

        //   if (currentCredit.token === "") {
        //     if (creditInfo.role === "admin") {
        //       currentCredit.credits -= CREDITS_PER_IMAGE;
        //     } else {
        //       currentCredit.used_today -= CREDITS_PER_IMAGE;
        //     }
        //     saveUserCredit(currentCredit);
        //     setCreditInfo({ ...currentCredit });
        //   } else {
        //     onGetUserCredits();
        //   }
        // } else if (data.error) {
        //   setFetchErrorMessage(JSON.stringify(data.error));
        // } else {
        //   setFetchErrorMessage("Received incorrect response data");
        // }
      })
      .catch((error) => {
        setIsLoading(false);
        if (error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          const errorObject = errors["INVALID_ARGUMENT"];
          setFetchErrorMessage({
            prompt: message,
            title: errorObject.title,
            message: errorObject.message,
          });

          const imageGenerateEndTime = new Date().getTime();
          // setFetchErrorMessage(error?.message);
          const params: any = {
            action: "image_generation_completed",
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: false,
            aspect_ratio: aspectRatio.value,
            sample_count: sampleCount.value,
            category: "ImageGenerationFailed",
          };

          ReactGA.event(params);
          MixpanelAnalytics.trackEvent("image_generation_completed", {
            generation_time: imageGenerateEndTime - imageGenerateStartTime,
            model_id: generateModel,
            success: false,
            aspect_ratio: aspectRatio.value,
            sample_count: sampleCount.value,
          });
          console.error("Error loading animation:", error);
        }
      });
  };

  const resetErrorMessage = () => setFetchErrorMessage(undefined);

  return {
    generationImages,
    isEditingImage,
    fetchErrorMessage,
    isLoading,
    disabled: isGenerateButtonDisabled,
    resetErrorMessage,
    setGenerationImages,
    onTestGenerateImageFetch,
    onGenerateImage,
  };
};
